import React, { useState, useEffect } from 'react';
import { User, Plus, Search, Star, TrendingUp, AlertCircle, Calendar, Phone, Mail, Award, X, Trash2, FileText, Upload, Shield, RotateCcw } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { validateDocument, getValidationClass, getValidationMessage } from '../utils/validators';
import DriverDocumentsTab from '../components/drivers/DriverDocumentsTab';

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('dados-gerais');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [clients, setClients] = useState([]);
  const [vehiclesOfClient, setVehiclesOfClient] = useState([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    rg: '',
    cnh: '',
    cnh_category: '',
    cnh_expiration: '',
    mopp_expiration: '',
    address: '',
    emergency_contact: '',
    notes: '',
    driver_type: 'driver',
    client_id: '',
    require_face_verification: false
  });
  const [documentTouched, setDocumentTouched] = useState(false);
  const [newUserCredentials, setNewUserCredentials] = useState(null);

  useEffect(() => {
    loadDrivers();
    loadClients();
  }, [filterStatus]);

  const loadClients = async () => {
    try {
      const response = await api.get('/api/clients');
      setClients(response.data.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadVehiclesOfClient = async (clientId) => {
    if (!clientId) { setVehiclesOfClient([]); return; }
    try {
      const response = await api.get(`/api/drivers/clients/${clientId}/vehicles`);
      setVehiclesOfClient(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos do cliente:', error);
      setVehiclesOfClient([]);
    }
  };

  const toggleVehicle = (vehicleId) => {
    setSelectedVehicleIds(prev =>
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await api.get('/api/drivers', { params });
      setDrivers(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar motoristas:', error);
      toast.error('Erro ao carregar motoristas');
    } finally {
      setLoading(false);
    }
  };

  const loadDriverDetails = async (id) => {
    try {
      const response = await api.get(`/api/drivers/${id}`);
      setSelectedDriver(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do motorista');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar CPF antes de enviar
    if (formData.document && !validateDocument(formData.document)) {
      toast.error('CPF inválido! Por favor, corrija antes de salvar.');
      return;
    }

    try {
      if (selectedDriver) {
        await api.put(`/api/drivers/${selectedDriver.id}`, { ...formData, vehicle_ids: selectedVehicleIds });
        toast.success('Motorista atualizado com sucesso!');
        setShowModal(false);
        resetForm();
        loadDrivers();
      } else {
        const dataToSend = {
          ...formData,
          vehicle_ids: selectedVehicleIds,
          admission_date: formData.admission_date || new Date().toISOString().split('T')[0]
        };
        const response = await api.post('/api/drivers', dataToSend);
        const isOperator = formData.driver_type === 'operator';
        toast.success(isOperator ? 'Vistoriador cadastrado com sucesso!' : 'Motorista cadastrado com sucesso!');
        setShowModal(false);
        resetForm();
        loadDrivers();
        if (isOperator && response.data?.data?.userCredentials) {
          setNewUserCredentials(response.data.data.userCredentials);
        }
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar motorista');
    }
  };

  const handleEdit = async (driver) => {
    setFormData({
      name: driver.name,
      email: driver.email || '',
      phone: driver.phone,
      document: driver.document,
      rg: driver.rg || '',
      cnh: driver.cnh,
      cnh_category: driver.cnh_category,
      cnh_expiration: driver.cnh_expiration ? driver.cnh_expiration.split('T')[0] : '',
      mopp_expiration: driver.mopp_expiration ? driver.mopp_expiration.split('T')[0] : '',
      address: driver.address || '',
      emergency_contact: driver.emergency_contact || '',
      notes: driver.notes || '',
      driver_type: driver.driver_type || 'driver',
      client_id: driver.client_id || '',
      require_face_verification: driver.require_face_verification || false
    });
    setSelectedDriver(driver);
    setShowModal(true);

    // Carregar veículos da empresa e os já vinculados ao motorista
    if (driver.client_id) {
      await loadVehiclesOfClient(driver.client_id);
      try {
        const res = await api.get(`/api/drivers/${driver.id}/vehicles`);
        setSelectedVehicleIds((res.data.data || []).map(v => v.id));
      } catch {
        setSelectedVehicleIds([]);
      }
    } else {
      setVehiclesOfClient([]);
      setSelectedVehicleIds([]);
    }
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Tem certeza que deseja excluir o motorista ${driver.name}?`)) {
      return;
    }

    try {
      await api.delete(`/api/drivers/${driver.id}`);
      toast.success('Motorista excluído com sucesso!');
      loadDrivers();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir motorista');
    }
  };

  const handleFaceReset = async (driver) => {
    if (!window.confirm(`Tem certeza que deseja resetar a biometria facial de ${driver.name}? O motorista precisara refazer o cadastro de fotos no proximo acesso ao aplicativo.`)) {
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.post(`/api/driver-face/${driver.id}/face-reset`, {
        userId: user.id
      });
      toast.success('Biometria facial resetada! O motorista devera refazer o cadastro de fotos no proximo login.');
      loadDrivers();
    } catch (error) {
      console.error('Erro ao resetar biometria:', error);
      toast.error(error.response?.data?.message || 'Erro ao resetar biometria facial');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      document: '',
      rg: '',
      cnh: '',
      cnh_category: '',
      cnh_expiration: '',
      mopp_expiration: '',
      address: '',
      emergency_contact: '',
      notes: '',
      driver_type: 'driver',
      client_id: '',
      require_face_verification: false
    });
    setSelectedDriver(null);
    setActiveTab('dados-gerais');
    setUploadedFile(null);
    setDocumentTouched(false);
    setVehiclesOfClient([]);
    setSelectedVehicleIds([]);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Apenas arquivos PDF, JPG ou PNG são permitidos');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 5MB');
        return;
      }

      setUploadedFile(file);
      toast.success('Arquivo carregado. Clique em "Ler CNH" para extrair os dados.');
    }
  };

  const handleCNHOCR = async () => {
    if (!uploadedFile) {
      toast.error('Por favor, selecione um arquivo primeiro');
      return;
    }

    setIsProcessingOCR(true);
    toast.loading('Processando CNH... Isso pode levar até 2 minutos.', { duration: 120000 });
    const formDataOCR = new FormData();
    formDataOCR.append('document', uploadedFile);

    try {
      const response = await api.post('/api/ocr/process-cnh', formDataOCR, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 120 segundos para OCR processar
      });

      if (response.data.success) {
        const cnhData = response.data.data;

        console.log('📄 CNH Data recebida do backend:', cnhData);

        // Auto-fill form fields with extracted data
        const updatedFields = {
          name: cnhData.nome || formData.name,
          document: cnhData.cpf || formData.document,
          rg: cnhData.rg || formData.rg,
          cnh: cnhData.registro || formData.cnh,
          cnh_category: cnhData.categoria || formData.cnh_category,
          cnh_expiration: cnhData.validade || formData.cnh_expiration,
        };

        console.log('✏️ Campos que serão atualizados:', updatedFields);

        setFormData(prev => ({
          ...prev,
          ...updatedFields
        }));

        toast.dismiss(); // Remove loading toast
        toast.success(`CNH processada! ${response.data.fieldsExtracted} campos extraídos.`);

        // Switch to Dados Gerais tab to show auto-filled fields
        setActiveTab('dados-gerais');
      } else {
        toast.error('Não foi possível extrair dados do documento');
      }
    } catch (error) {
      console.error('Erro ao processar CNH:', error);
      toast.dismiss(); // Remove loading toast
      toast.error(error.response?.data?.error || 'Erro ao processar CNH');
    } finally {
      setIsProcessingOCR(false);
      toast.dismiss(); // Remove loading toast
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-blue-600 bg-blue-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'text-green-600 bg-green-100';
    if (status === 'on_leave') return 'text-yellow-600 bg-yellow-100';
    if (status === 'suspended') return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Ativo',
      inactive: 'Inativo',
      on_leave: 'Afastado',
      suspended: 'Suspenso'
    };
    return labels[status] || status;
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.document.includes(searchTerm) ||
    driver.cnh.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Motorista/Vistoriador
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gestão completa da equipe de motoristas e vistoriadores
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Novo Cadastro
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total de Motoristas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {drivers.length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Ativos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {drivers.filter(d => d.status === 'active').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Score Médio</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {drivers.length > 0
                  ? (drivers.reduce((sum, d) => sum + (Number(d.current_score) || 0), 0) / drivers.length).toFixed(1)
                  : '0'}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Docs Vencendo</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {drivers.filter(d => {
                  const cnhDays = Math.ceil((new Date(d.cnh_expiration) - new Date()) / (1000 * 60 * 60 * 24));
                  return cnhDays <= 30;
                }).length}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, CPF ou CNH..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">Todos os Status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
          <option value="on_leave">Afastados</option>
          <option value="suspended">Suspensos</option>
        </select>
      </div>

      {/* Drivers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Empresa</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contato</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">CNH / Validade</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Score</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredDrivers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  {searchTerm ? 'Nenhum motorista encontrado' : 'Nenhum motorista cadastrado'}
                </td>
              </tr>
            ) : filteredDrivers.map((driver) => {
              const cnhDays = driver.cnh_expiration
                ? Math.ceil((new Date(driver.cnh_expiration) - new Date()) / (1000 * 60 * 60 * 24))
                : null;
              const cnhWarning = cnhDays !== null && cnhDays <= 30;
              return (
                <tr key={driver.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  {/* Nome */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{driver.name}</span>
                    </div>
                  </td>
                  {/* Tipo */}
                  <td className="px-4 py-3">
                    {driver.driver_type === 'operator' ? (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">Vistoriador</span>
                    ) : driver.driver_type === 'manager' ? (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Gestor</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Motorista</span>
                    )}
                  </td>
                  {/* Empresa */}
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[160px] truncate">
                    {driver.client_company_name || driver.client_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  {/* Contato */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700 dark:text-gray-300">{driver.phone}</div>
                    {driver.email && <div className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[180px]">{driver.email}</div>}
                  </td>
                  {/* CNH / Validade */}
                  <td className="px-4 py-3 text-sm">
                    {driver.driver_type !== 'operator' ? (
                      <div>
                        <span className="text-gray-700 dark:text-gray-300">{driver.cnh_category || '—'}</span>
                        {driver.cnh_expiration && (
                          <div className={`text-xs mt-0.5 ${cnhWarning ? 'text-red-500 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
                            {cnhWarning && <AlertCircle className="w-3 h-3 inline mr-0.5" />}
                            {new Date(driver.cnh_expiration).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  {/* Score */}
                  <td className="px-4 py-3">
                    {driver.current_score !== null && driver.current_score !== undefined ? (
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${getScoreColor(Number(driver.current_score))}`}>
                        {Number(driver.current_score).toFixed(0)}
                      </span>
                    ) : <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>}
                  </td>
                  {/* Status */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(driver.status)}`}>
                      {getStatusLabel(driver.status)}
                    </span>
                  </td>
                  {/* Ações */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(driver)}
                        className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(driver)}
                        className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {selectedDriver ? 'Editar Motorista' : 'Novo Motorista'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('dados-gerais')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'dados-gerais'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Dados Gerais
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('documentos')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'documentos'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documentos
                  </div>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Dados Gerais Tab */}
                {activeTab === 'dados-gerais' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CPF *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.document}
                      onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                      onBlur={() => setDocumentTouched(true)}
                      placeholder="000.000.000-00"
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${getValidationClass(formData.document, documentTouched) || 'border-gray-300 dark:border-gray-600'}`}
                    />
                    {documentTouched && formData.document && (
                      <p className={`text-xs mt-1 ${getValidationMessage(formData.document, documentTouched).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {getValidationMessage(formData.document, documentTouched).message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      RG
                    </label>
                    <input
                      type="text"
                      value={formData.rg}
                      onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                      placeholder="12.345.678-9"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Telefone *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo de Acesso *
                    </label>
                    <select
                      value={formData.driver_type}
                      onChange={(e) => setFormData({ ...formData, driver_type: e.target.value, client_id: '' })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="driver">Motorista</option>
                      <option value="manager">Gestor da Empresa</option>
                      <option value="operator">Vistoriador (Equipe VIPLOC)</option>
                    </select>
                    {formData.driver_type === 'operator' && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                        Funcionário interno VIPLOC. CNH não obrigatória. Será criada conta de acesso ao app automaticamente.
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Empresa Locatária
                    </label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => {
                        const clientId = e.target.value;
                        setFormData({ ...formData, client_id: clientId });
                        setSelectedVehicleIds([]);
                        loadVehiclesOfClient(clientId);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Sem empresa vinculada</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.company_name || client.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Vincula o motorista/vistoriador a uma locatária.
                    </p>
                  </div>

                  {formData.client_id && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Caminhões com Acesso às Vistorias
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Selecione quais caminhões este motorista poderá ver as vistorias no app. Múltiplos motoristas podem ter acesso ao mesmo caminhão.
                      </p>
                      {vehiclesOfClient.length === 0 ? (
                        <p className="text-xs text-orange-500 dark:text-orange-400">Nenhum caminhão com contrato ativo nesta empresa.</p>
                      ) : (
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                          {vehiclesOfClient.map(v => (
                            <label key={v.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-0">
                              <input
                                type="checkbox"
                                checked={selectedVehicleIds.includes(v.id)}
                                onChange={() => toggleVehicle(v.id)}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span className="text-sm text-gray-800 dark:text-gray-200">
                                <span className="font-medium">{v.license_plate}</span>
                                {v.brand || v.model ? ` — ${[v.brand, v.model].filter(Boolean).join(' ')}` : ''}
                                {v.year ? ` (${v.year})` : ''}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Validação Biométrica na Vistoria
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={formData.require_face_verification}
                          onChange={(e) => setFormData({ ...formData, require_face_verification: e.target.checked })}
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${formData.require_face_verification ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.require_face_verification ? 'translate-x-5' : ''}`} />
                        </div>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {formData.require_face_verification ? 'Obrigatória — o operador deve passar pela verificação facial antes de finalizar a vistoria' : 'Opcional — vistoria pode ser finalizada sem verificação facial'}
                      </span>
                    </label>
                  </div>

                  {formData.driver_type !== 'operator' && (<>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número da CNH *
                    </label>
                    <input
                      type="text"
                      required={formData.driver_type !== 'operator'}
                      value={formData.cnh}
                      onChange={(e) => setFormData({ ...formData, cnh: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Categoria CNH *
                    </label>
                    <input
                      type="text"
                      required={formData.driver_type !== 'operator'}
                      value={formData.cnh_category}
                      onChange={(e) => setFormData({ ...formData, cnh_category: e.target.value.toUpperCase() })}
                      placeholder="Ex: A, B, C, D, E, AB, AC, AD, AE"
                      maxLength="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Validade CNH *
                    </label>
                    <input
                      type="date"
                      required={formData.driver_type !== 'operator'}
                      value={formData.cnh_expiration}
                      onChange={(e) => setFormData({ ...formData, cnh_expiration: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Validade MOPP
                    </label>
                    <input
                      type="date"
                      value={formData.mopp_expiration}
                      onChange={(e) => setFormData({ ...formData, mopp_expiration: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  </>)}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contato de Emergência
                    </label>
                    <input
                      type="text"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                </div>
                )}

                {/* Documentos Tab */}
                {activeTab === 'documentos' && (
                  <DriverDocumentsTab
                    driverId={selectedDriver?.id}
                    onCNHProcessed={(cnhData) => {
                      // Log para debug
                      console.log('📄 Dados da CNH recebidos:', cnhData);
                      console.log('📝 Campos disponíveis:', Object.keys(cnhData));

                      // Preencher dados do formulário com os dados extraídos da CNH
                      // Tentar múltiplas variações de nomes de campos
                      const updatedFields = {
                        name: cnhData.nome || cnhData.name || formData.name,
                        document: cnhData.cpf || cnhData.document || formData.document,
                        rg: cnhData.rg || cnhData.identidade || formData.rg,
                        cnh: cnhData.registro || cnhData.cnh || cnhData.numero || cnhData.numeroCNH || formData.cnh,
                        cnh_category: cnhData.categoria || cnhData.category || cnhData.categoriaCNH || formData.cnh_category,
                        cnh_expiration: cnhData.validade || cnhData.vencimento || cnhData.dataValidade || formData.cnh_expiration,
                      };

                      console.log('✅ Campos que serão preenchidos:', updatedFields);

                      setFormData(prev => ({
                        ...prev,
                        ...updatedFields
                      }));

                      // Mudar para a aba de Dados Gerais para mostrar os campos preenchidos
                      setActiveTab('dados-gerais');
                    }}
                  />
                )}

                {/* Biometria Facial - Apenas para edicao */}
                {selectedDriver && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <h4 className="font-medium text-purple-900 dark:text-purple-100">Biometria Facial</h4>
                    </div>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                      Gerencie o cadastro de biometria facial do motorista no aplicativo mobile.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleFaceReset(selectedDriver)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Resetar Biometria Facial
                    </button>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                      * O motorista precisara refazer as fotos de cadastro no proximo acesso ao app
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {selectedDriver ? 'Atualizar' : 'Cadastrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Credentials dialog for new operator */}
      {newUserCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Acesso ao Aplicativo</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Credenciais geradas automaticamente</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Anote as credenciais abaixo. A senha não poderá ser recuperada posteriormente.
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 mb-5">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email / Login</p>
                <p className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{newUserCredentials.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Senha</p>
                <p className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{newUserCredentials.password}</p>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Tipo de acesso no app: <strong>Equipe Interna (Operador)</strong>
              </div>
            </div>
            <button
              onClick={() => setNewUserCredentials(null)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversPage;